import { useState, useEffect, Fragment } from 'react';
import { X, Plus, Trash2, Loader2, AlertCircle } from 'lucide-react';
import { AxiosError } from 'axios';
import { Button } from '../ui/button';
import SearchableSelect from '../ui/SearchableSelect';
import { purchasingAPI, ContractPrice, ContractType, coreAPI, fetchAllPages } from '../../lib/api';
import { usePreferences } from '../../contexts/PreferencesContext';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';

/**
 * CR-5477: backend ContractPrice / ContractPricePart validation returns
 * field-level errors under ``details.fields`` (e.g.
 * ``{"parts":[{"qty_from":["..."]}]}``). The shared getApiErrorMessage()
 * util collapses everything to the generic ``message`` ("Validation failed"),
 * which is why users saw "Failed to save contract" with no clue which field
 * was wrong. Drill into the per-field structure here to surface the real
 * cause without changing the shared util (used in 50+ places).
 */
function flattenContractPriceErrors(err: unknown): string | null {
  if (!(err instanceof AxiosError)) return null;
  const data = err.response?.data as Record<string, unknown> | undefined;
  const details = data?.['details'] as Record<string, unknown> | undefined;
  const fields = details?.['fields'] as Record<string, unknown> | undefined;
  if (!fields) return null;

  const messages: string[] = [];
  Object.entries(fields).forEach(([field, value]) => {
    if (field === 'parts' && Array.isArray(value)) {
      value.forEach((partErrors, idx) => {
        // CR-6037 rework: cross-part rules (e.g. overlapping qty bands) are
        // raised as ``ValidationError({'parts': ['Part X: …overlap…']})`` —
        // a flat list of STRINGS, not the per-row ``{qty_from: [...]}`` field
        // objects this loop was written for. Without handling them the whole
        // flatten returned null and the modal fell back to the useless
        // generic "Validation failed" toast, so the user couldn't tell WHY
        // the save was refused ("Update Contract does nothing"). Surface the
        // string verbatim.
        if (typeof partErrors === 'string') {
          messages.push(partErrors);
          return;
        }
        if (Array.isArray(partErrors)) {
          (partErrors as unknown[]).forEach(pe => messages.push(String(pe)));
          return;
        }
        if (partErrors && typeof partErrors === 'object') {
          Object.entries(partErrors as Record<string, unknown>).forEach(([pf, pv]) => {
            // Skip the "contract: required" noise — that's the nested-write
            // false positive we now strip on the backend; if anything still
            // surfaces it the user can't act on it.
            if (pf === 'contract') return;
            const msg = Array.isArray(pv) ? (pv as unknown[]).join(' ') : String(pv);
            messages.push(`Part ${idx + 1} — ${pf}: ${msg}`);
          });
        }
      });
    } else {
      const msg = Array.isArray(value) ? (value as unknown[]).join(' ') : String(value);
      messages.push(`${field}: ${msg}`);
    }
  });
  return messages.length ? messages.join(' • ') : null;
}

interface ContractPriceModalProps {
  isOpen: boolean;
  onClose: () => void;
  contract: ContractPrice | null;
  // CR-6037 rework: caller passes this to refresh any sibling view still
  // showing the saved contract (e.g. the Parts Details modal sitting behind
  // this edit modal). Fired after a successful save, before onClose.
  onSaved?: () => void;
}

interface PartRow {
  id?: number;
  part_number: string;
  description: string;
  unit_price: string;
  lot_price: string;
  qty_from: string;
  qty_to: string;
  unit_of_measure: string;
}

interface Company {
  id: number;
  name: string;
  company_type: string;
}

export default function ContractPriceModal({ isOpen, onClose, contract, onSaved }: ContractPriceModalProps) {
  const isEditing = !!contract;
  const { preferences } = usePreferences();
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  // CR-1441: enabled contract types for the (mandatory) Add-Contract picker.
  const [contractTypes, setContractTypes] = useState<ContractType[]>([]);

  // Form state — default currency from user preferences
  const [formData, setFormData] = useState({
    contract_number: '',
    supplier_contract_reference: '',
    contract_type: '',
    customer: '',
    supplier: '',
    valid_from: '',
    valid_to: '',
    currency: preferences.currency || 'GBP',
    status: 'draft',
    notes: '',
  });

  const [parts, setParts] = useState<PartRow[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // CR-6037 AC5: the authoritative, always-visible failure surface. Backend
  // rejections (e.g. overlapping qty bands -> HTTP 400) used to reach the user
  // only through a sonner toast, which is invisible under the nonce-locked
  // style-src CSP (same family as CR-4517) and short-lived anyway — so the
  // user just saw "Update Contract does nothing". This inline banner renders
  // with bundled Tailwind classes (no runtime-injected <style>, no inline
  // style attr), so it survives CSP and persists until the user edits a field.
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Load companies for the Customer / Supplier dropdowns.
  // CR-6037 rework: the plain /core/companies/ list is gated to Helix admins
  // (CompanyViewSet = IsHelixUser), so customer users got a 403 and both
  // dropdowns were empty in BOTH create and edit. Use the customer-safe
  // for-contract-pricing action, which is scoped server-side per portal role
  // (customer → own sites + traded suppliers; supplier → own sites + served
  // customers; helix → all). Returns company_type so the split below works.
  // CR-6166: re-fetch whenever the selected customer/site changes so the
  // Supplier dropdown re-scopes to that site's approved suppliers (the
  // division/group cascade). With no customer selected the endpoint returns the
  // full in-scope set; the Customer dropdown list is unaffected either way.
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const customerId = formData.customer ? parseInt(formData.customer) : undefined;
        const response = await coreAPI.getContractPricingCompanies(customerId);
        setCompanies(response.data.results || []);
      } catch (err) {
        console.error('Failed to load companies:', err);
      }
    };
    fetchCompanies();
  }, [formData.customer]);

  // CR-1441: load the enabled contract types for the (mandatory) Add picker.
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        // Walk pages — the backend clamps page_size, so one request silently truncated.
        const list = await fetchAllPages<ContractType>((page) => purchasingAPI.getContractTypes({ enabled: true, page_size: 100, page }));
        setContractTypes(list);
      } catch (err) {
        console.error('Failed to load contract types:', err);
      }
    };
    fetchTypes();
  }, []);

  // Load contract data when editing
  useEffect(() => {
    setSubmitError(null);
    if (contract) {
      setFormData({
        contract_number: contract.contract_number,
        supplier_contract_reference: contract.supplier_contract_reference || '',
        contract_type: contract.contract_type ? String(contract.contract_type) : '',
        customer: String(contract.customer),
        supplier: String(contract.supplier),
        valid_from: contract.valid_from,
        valid_to: contract.valid_to,
        currency: contract.currency,
        status: contract.status,
        notes: contract.notes || '',
      });

      // Load parts
      if (contract.parts && contract.parts.length > 0) {
        setParts(contract.parts.map(p => ({
          id: p.id,
          part_number: p.part_number,
          description: p.description || '',
          unit_price: p.unit_price || '',
          lot_price: p.lot_price || '',
          qty_from: p.qty_from,
          qty_to: p.qty_to,
          unit_of_measure: p.unit_of_measure,
        })));
      }
    } else {
      // Reset form for new contract
      setFormData({
        contract_number: '',
        supplier_contract_reference: '',
        contract_type: '',
        customer: '',
        supplier: '',
        valid_from: '',
        valid_to: '',
        currency: preferences.currency || 'GBP',
        status: 'draft',
        notes: '',
      });
      setParts([]);
    }
  }, [contract]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    // Clear the inline banner once the user starts fixing the input — a stale
    // "save failed" message next to an edited form reads as a lie.
    if (submitError) setSubmitError(null);
  };

  // CR-6166: changing the customer/site re-scopes the Supplier dropdown (the
  // effect above refetches), so clear any previously-picked supplier — it may
  // not be an approved supplier for the newly selected site.
  const handleCustomerChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      customer: value,
      supplier: value !== prev.customer ? '' : prev.supplier,
    }));
    if (errors['customer']) setErrors(prev => ({ ...prev, customer: '' }));
    if (errors['supplier']) setErrors(prev => ({ ...prev, supplier: '' }));
    if (submitError) setSubmitError(null);
  };

  const addPart = () => {
    // CR-5477: backend ContractPricePart validators require qty_from / qty_to
    // >= 0.001 (see MinValueValidator on the model). Defaulting both to '0'
    // produced a 400 "Ensure this value is greater than or equal to 0.001"
    // the moment a user clicked Add Part + Create Contract, blocking
    // manual Price Book creation.
    setParts(prev => [...prev, {
      part_number: '',
      description: '',
      unit_price: '',
      lot_price: '',
      qty_from: '1',
      qty_to: '1',
      unit_of_measure: 'EA',
    }]);
  };

  const removePart = (index: number) => {
    setParts(prev => prev.filter((_, i) => i !== index));
  };

  const updatePart = (index: number, field: string, value: string) => {
    setParts(prev => prev.map((part, i) =>
      i === index ? { ...part, [field]: value } : part
    ));
    if (submitError) setSubmitError(null);
  };

  // Holds the errors map for the most recent validate() call so the submit
  // handler can surface the specific first error in the toast without
  // waiting for the next render of setErrors().
  let latestErrors: Record<string, string> = {};

  const collectFirstError = (): string | null => {
    const values = Object.values(latestErrors).filter(Boolean);
    return values.length ? values[0] : null;
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // CR-1441: the contract number is auto-generated, so it's no longer a
    // required user input. Contract type IS required on Add (it drives the
    // number prefix); on Edit the type is immutable so we don't validate it.
    if (!isEditing && !formData.contract_type) {
      newErrors['contract_type'] = 'Contract type is required';
    }
    if (!formData.customer) {
      newErrors['customer'] = 'Customer is required';
    }
    // CR-5477: backend ContractPrice.supplier is non-nullable
    // (on_delete=PROTECT); submitting without it returned a 400 the
    // form silently rolled into a generic "Failed to save" toast,
    // blocking the entire Price Book → Contract setup workflow.
    if (!formData.supplier) {
      newErrors['supplier'] = 'Supplier is required';
    }
    if (!formData.valid_from) {
      newErrors['valid_from'] = 'Valid from date is required';
    }
    if (!formData.valid_to) {
      newErrors['valid_to'] = 'Valid to date is required';
    }
    if (formData.valid_from && formData.valid_to && formData.valid_from > formData.valid_to) {
      newErrors['valid_to'] = 'Valid to must be after valid from';
    }

    // Validate parts
    parts.forEach((part, index) => {
      if (!part.part_number.trim()) {
        newErrors[`part_${index}_part_number`] = 'Part number is required';
      }
      if (!part.unit_price && !part.lot_price) {
        newErrors[`part_${index}_price`] = 'Either unit price or lot price is required';
      }
      // CR-5477: ContractPricePart model has MinValueValidator(0.001) on both
      // qty_from and qty_to. Catch this up front so users see a clear field-
      // level message instead of the generic "Failed to save" toast that hides
      // a backend 400 the modal could have surfaced itself.
      const qtyFromNum = parseFloat(part.qty_from);
      const qtyToNum = parseFloat(part.qty_to);
      if (!part.qty_from || Number.isNaN(qtyFromNum) || qtyFromNum < 0.001) {
        newErrors[`part_${index}_qty`] = 'Qty from must be at least 0.001';
      } else if (!part.qty_to || Number.isNaN(qtyToNum) || qtyToNum < 0.001) {
        newErrors[`part_${index}_qty`] = 'Qty to must be at least 0.001';
      } else if (qtyFromNum > qtyToNum) {
        newErrors[`part_${index}_qty`] = 'Qty from must be <= qty to';
      }
    });

    setErrors(newErrors);
    latestErrors = newErrors;
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!validate()) {
      // CR-5477: surface the actual first field error.
      // The qty inputs in the parts table don't render an inline message
      // (only a red border), so the previous generic "Please fix the
      // validation errors" left users unable to see WHY save was refused.
      // We capture the freshly-computed errors here because setErrors is
      // batched and the closure over `errors` lags by one render.
      const firstMessage = collectFirstError();
      const message = firstMessage || 'Please fix the highlighted fields before saving.';
      // CR-6037 AC5: banner is the authoritative surface; toast is best-effort.
      setSubmitError(message);
      toast.error(message);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        // CR-1441: contract_number is auto-generated server-side (never sent).
        // Customer review 11 Sep (item 23): on edit the type is sent only
        // when changed; the number is never regenerated.
        // CR-6424: this modal creates PRICEBOOK contracts; realm is immutable
        // after creation so it is only sent on create.
        ...(isEditing ? (
          formData.contract_type && formData.contract_type !== String(contract?.contract_type ?? '')
            ? { contract_type: parseInt(formData.contract_type) } : {}
        ) : {
          contract_type: formData.contract_type ? parseInt(formData.contract_type) : undefined,
          realm: 'pricebook' as const,
        }),
        customer: parseInt(formData.customer),
        supplier: formData.supplier ? parseInt(formData.supplier) : undefined,
        valid_from: formData.valid_from,
        valid_to: formData.valid_to,
        currency: formData.currency,
        supplier_contract_reference: formData.supplier_contract_reference.trim(), // CR-7267
        status: formData.status as 'draft' | 'active' | 'expired',
        notes: formData.notes,
        parts: parts.map(p => ({
          ...(p.id ? { id: p.id } : {}),
          part_number: p.part_number,
          description: p.description,
          unit_price: p.unit_price || null,
          lot_price: p.lot_price || null,
          qty_from: p.qty_from,
          qty_to: p.qty_to,
          unit_of_measure: p.unit_of_measure,
        })),
      };

      if (isEditing && contract) {
        await purchasingAPI.updateContractPrice(contract.id, payload);
        toast.success('Contract updated successfully');
      } else {
        await purchasingAPI.createContractPrice(payload);
        toast.success('Contract created successfully');
      }

      onSaved?.();
      onClose();
    } catch (err: unknown) {
      console.error('Failed to save contract:', err);
      const fieldMessage = flattenContractPriceErrors(err);
      const message = fieldMessage || getApiErrorMessage(err, 'Failed to save contract');
      // CR-6037 AC5: render the rejection INLINE and persistently. The toast is
      // kept only as a secondary cue — it cannot be the sole channel because
      // the CSP strips sonner's runtime styles, so the user would otherwise get
      // no feedback at all (the original AC5 failure).
      setSubmitError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // CR-6037 rework: customer/supplier users can't list companies — the
  // /core/companies/ endpoint is gated to Helix admins (CompanyViewSet =
  // IsHelixUser), so fetchCompanies() above 403s and leaves `companies`
  // empty. When such a user edited a contract, the Customer and Supplier
  // dropdowns showed the blank "Select …" placeholder even though both
  // parties were set — the contract's real customer/supplier id was held in
  // formData but had no matching <option> to render. Seed an option from the
  // contract's own party (id + name, both carried on the detail payload) so
  // editing always displays — and round-trips — the correct parties for every
  // user type. Helix admins still get the full pickable list from companies.
  const withParty = (
    list: Company[],
    id?: number | null,
    name?: string | null,
  ): Company[] => {
    if (!id || list.some(c => c.id === id)) return list;
    return [{ id, name: name || `#${id}`, company_type: '' }, ...list];
  };
  const customers = withParty(
    companies.filter(c => c.company_type === 'customer'),
    contract?.customer,
    contract?.customer_name,
  );
  const suppliers = withParty(
    companies.filter(c => c.company_type === 'supplier'),
    contract?.supplier,
    contract?.supplier_name,
  );

  // CR-1441: options for the contract-type picker. On Edit the contract's own
  // type may have been disabled since (so it's absent from the enabled list) —
  // seed it so the locked dropdown still renders the correct label.
  const contractTypeOptions: ContractType[] = (
    isEditing && contract?.contract_type
      && !contractTypes.some(t => t.id === contract.contract_type)
  )
    ? [
        {
          id: contract.contract_type,
          name: contract.contract_type_name || `#${contract.contract_type}`,
          abbreviation: contract.contract_type_abbreviation || '',
          enabled: false,
        },
        ...contractTypes,
      ]
    : contractTypes;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">
            {isEditing ? 'Edit Contract Price' : 'Add Contract Price'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Contract Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Contract Number — CR-1441: auto-generated from the contract
                type's prefix, never user-entered. Read-only in both modes:
                blank with an info note on Add, shows the value on Edit. */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Contract Number
              </label>
              <input
                type="text"
                value={formData.contract_number}
                readOnly
                disabled
                title="Contract number will be automatically generated"
                className="w-full px-3 py-2 border border-border rounded-lg bg-muted text-muted-foreground cursor-not-allowed"
                placeholder={isEditing ? '' : 'Will be generated automatically'}
              />
              {!isEditing && (
                <p className="text-xs text-muted-foreground mt-1">
                  Contract number will be automatically generated.
                </p>
              )}
            </div>
            {/* CR-7267: the supplier's own reference for the same agreement — optional, not unique, labelled distinctly from our number. */}
            <div>
              <label htmlFor="supplier-contract-reference" className="block text-sm font-medium mb-1">
                Supplier's Contract Reference
              </label>
              <input
                id="supplier-contract-reference"
                type="text"
                value={formData.supplier_contract_reference}
                onChange={(e) => setFormData({ ...formData, supplier_contract_reference: e.target.value })}
                maxLength={100}
                placeholder="The supplier's own number (optional)"
                className="w-full px-3 py-2 border border-border rounded-lg"
              />
            </div>

            {/* Contract Type — CR-1441: mandatory on Add (drives the number
                prefix). Customer review 11 Sep (item 23): changeable on
                Edit; the number it generated stays as issued. */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Price Book / Contract Type {!isEditing && <span className="text-red-500">*</span>}
              </label>
              <select
                value={formData.contract_type}
                onChange={(e) => handleInputChange('contract_type', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-muted disabled:cursor-not-allowed disabled:opacity-70 ${errors['contract_type'] ? 'border-red-500' : 'border-border'}`}
              >
                <option value="">Select Contract Type</option>
                {contractTypeOptions.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.abbreviation})
                  </option>
                ))}
              </select>
              {errors['contract_type'] && (
                <p className="text-xs text-red-500 mt-1">{errors['contract_type']}</p>
              )}
              {isEditing && (
                <p className="text-xs text-muted-foreground mt-1">
                  Changing the type does not renumber the contract — it stays {formData.contract_number}.
                </p>
              )}
            </div>

            {/* Customer — CR: type-ahead search instead of scroll-only native select */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Customer <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                options={customers.map(c => ({ value: String(c.id), label: c.name }))}
                value={formData.customer}
                onChange={handleCustomerChange}
                placeholder="Select Customer"
                className={errors['customer'] ? '[&>button]:border-red-500' : ''}
              />
              {errors['customer'] && (
                <p className="text-xs text-red-500 mt-1">{errors['customer']}</p>
              )}
            </div>

            {/* Supplier — CR: type-ahead search instead of scroll-only native select */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Supplier <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                options={suppliers.map(s => ({ value: String(s.id), label: s.name }))}
                value={formData.supplier}
                onChange={(value) => handleInputChange('supplier', value)}
                placeholder="Select Supplier"
                className={errors['supplier'] ? '[&>button]:border-red-500' : ''}
              />
              {errors['supplier'] && (
                <p className="text-xs text-red-500 mt-1">{errors['supplier']}</p>
              )}
            </div>

            {/* Valid From */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Valid From <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.valid_from}
                onChange={(e) => handleInputChange('valid_from', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${errors['valid_from'] ? 'border-red-500' : 'border-border'}`}
              />
              {errors['valid_from'] && (
                <p className="text-xs text-red-500 mt-1">{errors['valid_from']}</p>
              )}
            </div>

            {/* Valid To */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Valid To <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.valid_to}
                onChange={(e) => handleInputChange('valid_to', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${errors['valid_to'] ? 'border-red-500' : 'border-border'}`}
              />
              {errors['valid_to'] && (
                <p className="text-xs text-red-500 mt-1">{errors['valid_to']}</p>
              )}
            </div>

            {/* Currency */}
            <div>
              <label className="block text-sm font-medium mb-1">Currency</label>
              <select
                value={formData.currency}
                onChange={(e) => handleInputChange('currency', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {/* CR-5477: align with backend ContractPrice.CURRENCY_CHOICES.
                    The hardcoded short list previously hid 5 currencies the
                    model already accepts (INR/AUD/CAD/CHF/MXN); customers
                    needing those currencies could not pick them in the modal
                    even though the backend would have stored them. */}
                <option value="GBP">GBP - British Pound</option>
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="JPY">JPY - Japanese Yen</option>
                <option value="CNY">CNY - Chinese Yuan</option>
                <option value="INR">INR - Indian Rupee</option>
                <option value="AUD">AUD - Australian Dollar</option>
                <option value="CAD">CAD - Canadian Dollar</option>
                <option value="CHF">CHF - Swiss Franc</option>
                <option value="MXN">MXN - Mexican Peso</option>
                <option value="PLN">PLN - Polish Złoty</option>
              </select>
            </div>

            {/* Status — backend ContractPrice.save() auto-derives status from
                the validity window for any contract whose dates cover today,
                so picking a value here would silently revert. Disable the
                dropdown in that case so the toast doesn't lie. The picker
                stays usable for future-dated contracts, where the user's
                choice between draft/active is the only thing save() honours
                (and the only case where the field is meaningful at all). */}
            {(() => {
              const today = new Date().toISOString().slice(0, 10);
              const inCurrentWindow = Boolean(
                formData.valid_from && formData.valid_to
                && formData.valid_from <= today
                && today <= formData.valid_to
              );
              return (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Status
                    {inCurrentWindow && (
                      <span className="text-muted-foreground font-normal ml-1">
                        — auto-derived while the dates cover today
                      </span>
                    )}
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    disabled={inCurrentWindow}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <option value="draft">Draft</option>
                    {/* CR-6424 (R4): the whole Pricing surface calls this
                        status "Live" — the edit dropdown was the one place
                        still saying "Active". The stored value is unchanged. */}
                    <option value="active">Live</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
              );
            })()}

            {/* Notes */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                rows={2}
                placeholder="Optional notes about this contract..."
              />
            </div>
          </div>

          {/* Parts Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">Contract Parts</h3>
              <Button variant="outline" size="sm" onClick={addPart}>
                <Plus className="h-4 w-4 mr-1" />
                Add Part
              </Button>
            </div>

            {parts.length === 0 ? (
              <div className="text-center py-8 bg-muted/30 rounded-lg border border-dashed">
                <p className="text-muted-foreground text-sm">No parts added yet</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={addPart}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add First Part
                </Button>
              </div>
            ) : (
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left py-2 px-3 font-medium min-w-[140px]">Part Number *</th>
                      <th className="text-left py-2 px-3 font-medium min-w-[160px]">Description</th>
                      <th className="text-right py-2 px-3 font-medium min-w-[120px]">Unit Price</th>
                      <th className="text-right py-2 px-3 font-medium min-w-[120px]">Lot Price</th>
                      <th className="text-right py-2 px-3 font-medium min-w-[110px]">Qty From *</th>
                      <th className="text-right py-2 px-3 font-medium min-w-[110px]">Qty To *</th>
                      <th className="text-center py-2 px-3 font-medium min-w-[80px]">UOM</th>
                      <th className="text-center py-2 px-3 font-medium w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {parts.map((part, index) => {
                      // CR-5477: collect any field errors for this part so we
                      // can render one inline message row below — qty / price
                      // inputs only had red borders before, leaving the user
                      // to guess which rule was broken (min 0.001, qty_from
                      // <= qty_to, missing price, etc.).
                      const partErrors = [
                        errors[`part_${index}_part_number`],
                        errors[`part_${index}_price`],
                        errors[`part_${index}_qty`],
                      ].filter(Boolean);
                      return (
                      <Fragment key={index}>
                      <tr className={partErrors.length ? '' : 'border-b last:border-b-0'}>
                        <td className="py-2 px-3">
                          <input
                            type="text"
                            value={part.part_number}
                            onChange={(e) => updatePart(index, 'part_number', e.target.value)}
                            className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-primary ${errors[`part_${index}_part_number`] ? 'border-red-500' : 'border-border'}`}
                            placeholder="PART-001"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="text"
                            value={part.description}
                            onChange={(e) => updatePart(index, 'description', e.target.value)}
                            className="w-full px-2 py-1 border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                            placeholder="Description"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="number"
                            step="0.0001"
                            value={part.unit_price}
                            onChange={(e) => updatePart(index, 'unit_price', e.target.value)}
                            className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-primary text-right ${errors[`part_${index}_price`] ? 'border-red-500' : 'border-border'}`}
                            placeholder="0.00"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="number"
                            step="0.0001"
                            value={part.lot_price}
                            onChange={(e) => updatePart(index, 'lot_price', e.target.value)}
                            className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-primary text-right ${errors[`part_${index}_price`] ? 'border-red-500' : 'border-border'}`}
                            placeholder="0.00"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="number"
                            value={part.qty_from}
                            onChange={(e) => updatePart(index, 'qty_from', e.target.value)}
                            className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-primary text-right ${errors[`part_${index}_qty`] ? 'border-red-500' : 'border-border'}`}
                          />
                        </td>
                        <td className="py-2 px-3">
                          <input
                            type="number"
                            value={part.qty_to}
                            onChange={(e) => updatePart(index, 'qty_to', e.target.value)}
                            className={`w-full px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-primary text-right ${errors[`part_${index}_qty`] ? 'border-red-500' : 'border-border'}`}
                          />
                        </td>
                        <td className="py-2 px-3 text-center">
                          <select
                            value={part.unit_of_measure}
                            onChange={(e) => updatePart(index, 'unit_of_measure', e.target.value)}
                            className="px-2 py-1 border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                          >
                            <option value="EA">EA</option>
                            <option value="KG">KG</option>
                            <option value="M">M</option>
                            <option value="L">L</option>
                            <option value="PC">PC</option>
                            <option value="SET">SET</option>
                          </select>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <button
                            onClick={() => removePart(index)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                      {partErrors.length > 0 && (
                        <tr className="border-b last:border-b-0">
                          <td colSpan={8} className="px-3 pb-2">
                            <p className="text-xs text-red-500">{partErrors.join(' • ')}</p>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 space-y-3">
          {/* CR-6037 AC5: persistent, CSP-safe failure banner. Sits in the
              non-scrolling footer right above the action buttons so the reason
              a save was refused is always visible at the point of action. */}
          {submitError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span className="whitespace-pre-line">{submitError}</span>
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                isEditing ? 'Update Contract' : 'Create Contract'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
